import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ProductGrid } from '../components/ProductGrid';
import { ProductFilters } from '../components/ProductFilters';
import { productsService } from '../services/products';
import { categoriesService } from '../services/categories';
import { ProductQueryParams } from '../types/woocommerce';

export const ProductsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<ProductQueryParams>(() => {
    const params: ProductQueryParams = {
      page: parseInt(searchParams.get('page') || '1'),
      per_page: 12,
    };

    // Récupérer les filtres depuis l'URL
    if (searchParams.get('orderby')) params.orderby = searchParams.get('orderby')!;
    if (searchParams.get('order')) params.order = searchParams.get('order') as 'asc' | 'desc';
    if (searchParams.get('category')) params.category = searchParams.get('category')!;
    if (searchParams.get('min_price')) params.min_price = searchParams.get('min_price')!;
    if (searchParams.get('max_price')) params.max_price = searchParams.get('max_price')!;
    if (searchParams.get('on_sale')) params.on_sale = searchParams.get('on_sale') === 'true';
    if (searchParams.get('featured')) params.featured = searchParams.get('featured') === 'true';
    if (searchParams.get('stock_status')) params.stock_status = searchParams.get('stock_status') as any;

    return params;
  });

  // Récupérer les produits
  const { data: productsData, isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ['products', filters],
    queryFn: () => productsService.getProducts(filters),
  });

  // Récupérer les catégories pour les filtres
  const { data: categoriesData } = useQuery({
    queryKey: ['categories', 'all'],
    queryFn: () => categoriesService.getCategories({ per_page: 100, hide_empty: true }),
  });

  // Mettre à jour l'URL quand les filtres changent
  useEffect(() => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value.toString());
      }
    });

    setSearchParams(params);
  }, [filters, setSearchParams]);

  const handleFiltersChange = (newFilters: ProductQueryParams) => {
    setFilters({ ...newFilters, page: 1 }); // Réinitialiser à la page 1 lors du changement de filtres
  };

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page });
    window.scrollTo(0, 0);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="container-custom py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Nos produits</h1>

        <div className="lg:grid lg:grid-cols-4 lg:gap-8">
          {/* Filtres */}
          <aside className="mb-8 lg:mb-0">
            <ProductFilters
              categories={categoriesData?.categories || []}
              currentFilters={filters}
              onFiltersChange={handleFiltersChange}
            />
          </aside>

          {/* Grille de produits */}
          <main className="lg:col-span-3">
            {/* Résumé des résultats */}
            {productsData && (
              <div className="flex items-center justify-between mb-6">
                <p className="text-sm text-gray-700">
                  Affichage de{' '}
                  <span className="font-medium">
                    {(filters.page! - 1) * filters.per_page! + 1}
                  </span>{' '}
                  à{' '}
                  <span className="font-medium">
                    {Math.min(filters.page! * filters.per_page!, productsData.pagination.total)}
                  </span>{' '}
                  sur{' '}
                  <span className="font-medium">{productsData.pagination.total}</span>{' '}
                  résultats
                </p>
              </div>
            )}

            <ProductGrid
              products={productsData?.products || []}
              loading={productsLoading}
              error={productsError as Error}
            />

            {/* Pagination */}
            {productsData && productsData.pagination.totalPages > 1 && (
              <div className="mt-8 flex justify-center">
                <nav className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(filters.page! - 1)}
                    disabled={filters.page === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Précédent
                  </button>

                  {/* Numéros de page */}
                  <div className="flex gap-1">
                    {[...Array(Math.min(5, productsData.pagination.totalPages))].map((_, idx) => {
                      let pageNum: number;
                      if (productsData.pagination.totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (filters.page! <= 3) {
                        pageNum = idx + 1;
                      } else if (filters.page! >= productsData.pagination.totalPages - 2) {
                        pageNum = productsData.pagination.totalPages - 4 + idx;
                      } else {
                        pageNum = filters.page! - 2 + idx;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-2 text-sm font-medium rounded-md ${
                            pageNum === filters.page
                              ? 'bg-primary-600 text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(filters.page! + 1)}
                    disabled={filters.page === productsData.pagination.totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Suivant
                  </button>
                </nav>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};
